/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { QRCodeSVG } from "qrcode.react";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentProps,
  type RefObject,
} from "react";

import { cssFns } from "@superweb/css";
import { useFlag } from "@superweb/flags";
import { Dialog, useTypo, useUiColors } from "@superweb/ui";

import { Message, useMessage } from "#intl";

import { EmptyStatePlaceholder } from "../empty-state-placeholder";
import { useErrorBoundaryOptions } from "../error-boundary-context";
import { useBrand } from "../brand-context";

import yandexBrandSvg from "./images/yandex.svg";
import yangoBrandSvg from "./images/yango.svg";

export const Unexpected = ({
  variant,
  traceId,
  clientDebugId,
  titleId,
  size,
  view = "inline",
  dialogProps,
  reset,
}: {
  variant?: "waitingMan";
  traceId?: string;
  clientDebugId?: string;
  titleId?: string;
  size?: "small" | "medium";
  reset?: () => void;
} & (
  | {
      view?: "inline";
      dialogProps?: never;
    }
  | {
      view: "dialog";
      dialogProps?: {
        size?: ComponentProps<typeof Dialog>["size"];
        onClose?: () => void;
      };
    }
)) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const uiColors = useUiColors();
  const typo = useTypo();
  const message = useMessage();
  const flag = useFlag();

  const enableMessengerSupportLink = flag({
    id: "ab2253d2-1781-49ed-8e74-2872d71a41b3",
    description: "Enable messenger support link in unexpected error",
  });

  const supportChatCreate = flag({
    id: "7eb2acf5-71fd-47d3-aea8-5d100b6f5676",
    description: "Support chat view",
  });

  const { makeSupportLinkDeprecated, makeSupportLink } =
    useErrorBoundaryOptions();

  const supportLink = enableMessengerSupportLink
    ? makeSupportLink?.({ traceId, clientDebugId })
    : makeSupportLinkDeprecated?.({ traceId, clientDebugId });

  const qrValue = traceId || clientDebugId;
  const shouldShowQrCode = qrValue;
  const shouldShowLink =
    supportChatCreate && supportLink && (clientDebugId || traceId);

  const title = message({
    id: "63cee1c0-f1bd-4245-9a6c-3cc0245fe030",
    context: "Common error. Something went wrong. Title",
    default: "Something went wrong",
  });

  const content = (
    <div
      css={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
      }}
    >
      <div ref={containerRef}>
        <EmptyStatePlaceholder
          {...(shouldShowQrCode
            ? {
                image: (
                  <div
                    css={{
                      display: "grid",
                      alignContent: "start",
                      justifyItems: "center",
                    }}
                  >
                    <QrCodeImage value={qrValue} containerRef={containerRef} />
                  </div>
                ),
              }
            : variant
              ? { variant }
              : { image: null })}
          size={size}
          title={view === "dialog" ? null : title}
          titleId={titleId}
          description={
            <div
              css={{
                display: "flex",
                flexDirection: "column",
                rowGap: "12px",
                maxWidth: "548px",
                ...cssFns.padding("0px", "6px"),
              }}
            >
              <Message
                id="ada3741a-6c41-4510-9050-8a0c3e6dd1f8"
                context="Common error. Something went wrong. Info"
                default="Try reloading, check back later, or contact Support"
              />
              {(traceId || clientDebugId) && (
                <span
                  css={{
                    color: uiColors.textMinor,
                    ...typo({
                      level: "body2",
                      density: "normal",
                      weight: "regular",
                    }),
                  }}
                >
                  {traceId || clientDebugId}
                </span>
              )}
            </div>
          }
          buttons={[
            ...(reset
              ? [
                  {
                    view: shouldShowLink ? "default" : "outline",
                    text: message({
                      id: "0597cc88-4cf5-4bec-bc13-80ef1e98ef26",
                      context:
                        "Common error. Something went wrong. Refresh button title",
                      default: "Reload",
                    }),
                    onPress: reset,
                  } as const,
                ]
              : []),
            ...(shouldShowLink
              ? [
                  {
                    view: "outline",
                    text: message({
                      id: "25a4f20c-3bb5-45c1-8d11-d2a109402616",
                      context:
                        "Common error. Something went wrong. Support link title",
                      default: "Support",
                    }),
                    link: {
                      href: supportLink.href,
                      external: true,
                    },
                  } as const,
                ]
              : []),
          ]}
        />
      </div>
    </div>
  );

  return view === "dialog" ? (
    <Dialog
      title={title}
      content={
        <div
          css={{
            display: "grid",
            height: shouldShowQrCode ? "452px" : "120px",
            marginBlockEnd: "16px",
            ...cssFns.placeContent("center"),
          }}
        >
          {content}
        </div>
      }
      onClose={dialogProps?.onClose}
      size={dialogProps?.size}
    />
  ) : (
    content
  );
};

const QRCodeSize = 110;
const brandDisplayConfig = {
  yandex: {
    qrCodeColor: "#9E9B98",
    qrCodeTop: 12,
    qrCodeLeft: 9,
  },
  yango: {
    qrCodeColor: "#96A1C0",
    qrCodeTop: 8,
    qrCodeLeft: 252,
  },
} satisfies Record<
  ReturnType<typeof useBrand>["brand"],
  {
    qrCodeColor: string;
    qrCodeTop: number;
    qrCodeLeft: number;
  }
>;

export const QrCodeImage = ({
  value,
  containerRef,
}: {
  value: string;
  containerRef: RefObject<HTMLDivElement>;
}) => {
  const uiColors = useUiColors();
  const { brand } = useBrand();
  const displayConfig = brandDisplayConfig[brand];

  const [imageState, setImageState] = useState<"loading" | "loaded" | "error">(
    "loading",
  );
  const [leadingParent, setLeadingParent] = useState<HTMLElement | null>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [isImageForcedHidden, setIsImageForcedHidden] = useState(true);
  const isImageActuallyVisible =
    imageState === "loaded" && !isImageForcedHidden;

  const onResize = useCallback(() => {
    if (
      containerRef.current === null ||
      containerRef.current.parentElement === null ||
      imageRef.current === null ||
      !imageRef.current.complete ||
      imageRef.current.naturalHeight === 0
    ) {
      return;
    }

    const containerHeight = containerRef.current.offsetHeight;
    const containerWidth = containerRef.current.offsetWidth;

    let containerParent = containerRef.current.parentElement;
    while (
      containerParent.offsetHeight === containerHeight &&
      containerParent.offsetWidth === containerWidth &&
      containerParent.parentElement
    ) {
      containerParent = containerParent.parentElement;
    }

    setLeadingParent(containerParent);

    const containerParentHeight = containerParent.offsetHeight;
    const containerParentWidth = containerParent.offsetWidth;
    const imageActualHeight = imageRef.current.naturalHeight;
    const imageActualWidth = imageRef.current.naturalWidth;
    const imageCurrentHeight = imageRef.current.offsetHeight;
    const imageCurrentWidth = imageRef.current.offsetWidth;
    const containerHeightWithImage =
      imageCurrentHeight === 0
        ? containerHeight - QRCodeSize + imageActualHeight
        : containerHeight;

    const containerWidthWithImage = imageActualWidth;

    if (imageCurrentHeight === 0 && imageCurrentWidth === 0) {
      if (
        containerParentHeight > containerHeightWithImage &&
        containerParentWidth > containerWidthWithImage
      ) {
        setIsImageForcedHidden(false);
      }
    } else {
      if (
        containerParentHeight < containerHeightWithImage ||
        containerParentWidth < containerWidthWithImage
      ) {
        setIsImageForcedHidden(true);
      }
    }
  }, [containerRef]);

  useEffect(() => {
    if (!containerRef.current?.parentElement) {
      return;
    }

    const observer = new ResizeObserver(onResize);
    observer.observe(containerRef.current.parentElement);

    if (!leadingParent || !leadingParent.contains(containerRef.current)) {
      return () => observer.disconnect();
    }

    let currentParent: HTMLElement | null = containerRef.current;
    do {
      currentParent = currentParent.parentElement;
      if (!currentParent) {
        break;
      }
      observer.observe(currentParent);
    } while (currentParent !== leadingParent);

    return () => observer.disconnect();
  }, [onResize, containerRef, leadingParent]);

  return (
    <div css={{ position: "relative" }}>
      {imageState !== "error" && (
        <img
          ref={imageRef}
          css={{
            pointerEvents: "none",
            userSelect: "none",
            ...(!isImageActuallyVisible && {
              opacity: "0",
              visibility: "hidden",
              height: "0",
              width: "0",
            }),
          }}
          src={{ yandex: yandexBrandSvg, yango: yangoBrandSvg }[brand]}
          alt=""
          onLoad={() => {
            setImageState("loaded");
            onResize();
          }}
          onError={() => setImageState("error")}
        />
      )}

      <div
        css={
          isImageActuallyVisible
            ? {
                position: "absolute",
                top: displayConfig.qrCodeTop + "px",
                left: displayConfig.qrCodeLeft + "px",
              }
            : undefined
        }
      >
        <QRCodeSVG
          value={value}
          level="L"
          size={QRCodeSize}
          bgColor="transparent"
          fgColor={
            isImageActuallyVisible
              ? displayConfig.qrCodeColor
              : uiColors.textMinor
          }
        />
      </div>
    </div>
  );
};
